import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Loader } from '../../../ui';
import { SalesStanding, fetchSalesStanding } from '../../../Reports/reportsApi';
import './sales.scss';

/**
 * "Where I stand": the agent's rank by revenue among the active agents of
 * their region (India / USA / ROW), month to date, with the gap to the next
 * place and a 6-month rank trend.
 *
 * artium-reports ranks everyone but sends back only this agent's own
 * position and the size of the gap, so nothing here can show another
 * agent's name or numbers.
 */

// Chart colours (apexcharts config only). The panel itself is styled in sales.scss with tokens.
const MUTED = '#9ca3af';
const LINE = '#ebd2f1'; // --ds-lavender
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const monthIndex = (ym: string): number => Number(ym.slice(5, 7)) - 1;

/** ₹15K, ₹1.2L, ₹2.4Cr. */
const short = (n: number): string => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n >= 1e8 ? 0 : 1).replace(/\.0$/, '')}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n >= 1e6 ? 0 : 1).replace(/\.0$/, '')}L`;
  if (n >= 1e3) return `₹${Math.round(n / 1e3)}K`;
  return `₹${Math.round(n)}`;
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** `fill` stretches the card to its row's height; only worth it when there's a ranking to show. */
const Shell: React.FC<{ title: string; children: React.ReactNode; fill?: boolean }> = ({ title, children, fill }) => (
  <section className={`sales-standing${fill ? ' sales-standing--fill' : ''}`}>
    <h2 className="sales-standing__title">{title}</h2>
    {children}
  </section>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="sales-standing__note sales-standing__muted">{children}</div>
);

export const WhereIStand: React.FC<{
  /** "YYYY-MM": the month ranked (month to date if current). */
  month: string;
  /** Admin only: whose standing. An agent always gets their own. */
  email?: string | null;
  /** "Where I Stand" for the agent; "Where they stand" for an admin looking at someone. */
  title?: string;
}> = ({ month, email, title = 'Where I Stand' }) => {
  const [data, setData] = useState<SalesStanding | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    fetchSalesStanding(month, email ?? undefined)
      .then((d) => { if (live) setData(d); })
      .catch((err: any) => { if (live) { setData(null); setError(err?.message || 'Could not load the ranking'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [month, email]);

  if (loading) return <Shell title={title}><Loader label="Loading the ranking…" /></Shell>;
  if (error) return <Shell title={title}><Note>{error}</Note></Shell>;
  if (!data || !data.inPool) {
    return (
      <Shell title={title}>
        <Note>
          <b className="sales-standing__strong">{data?.email ?? email}</b> isn&apos;t an active agent on the Sales Agents list, so there&apos;s
          no ranking. Check the email there matches this one exactly (it&apos;s how agents are matched), or add them on
          Sales → Sales Agents.
        </Note>
      </Shell>
    );
  }
  if (!data.region || !data.current) {
    return (
      <Shell title={title}>
        <Note>No region set, so there&apos;s no one to rank against. A sales admin sets the region (India, USA or ROW) on Sales → Sales Agents.</Note>
      </Shell>
    );
  }

  const c = data.current;
  const prevName = data.previous ? LONG_MONTHS[monthIndex(data.previous.month)] : '';
  const movement =
    data.change === null || !data.previous
      ? null
      : data.change > 0
        ? `Up ${plural(data.change, 'place')} since ${prevName}`
        : data.change < 0
          ? `Down ${plural(-data.change, 'place')} since ${prevName}`
          : `Same place as ${prevName}`;
  const gap = c.behindNext
    ? <b>{short(c.behindNext.amount)} behind #{c.behindNext.rank}</b>
    : c.aheadOfNext
      ? <b>{short(c.aheadOfNext.amount)} ahead of #2</b>
      : c.of === 1
        ? <b>The only {data.region} agent</b>
        : null;

  // Thin cells for a big region, so the bar always fits.
  const dense = c.of > 30;

  const trend = data.trend;
  const options: ApexOptions = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, background: 'transparent', fontFamily: 'inherit', parentHeightOffset: 0 },
    theme: { mode: 'dark' },
    colors: [LINE],
    stroke: { width: 2.5, curve: 'straight' },
    markers: {
      size: 5,
      strokeWidth: 0,
      discrete: [{ seriesIndex: 0, dataPointIndex: trend.length - 1, fillColor: '#fff', strokeColor: '#fff', size: 8 }],
    },
    dataLabels: {
      enabled: true,
      formatter: (v: number) => `#${v}`,
      offsetY: -8,
      background: { enabled: false },
      style: { fontSize: '12px', fontWeight: 500, colors: ['#d1d5db'] },
    },
    grid: { borderColor: '#2b2d35', strokeDashArray: 0, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } }, padding: { top: 18, left: 12, right: 12 } },
    xaxis: {
      categories: trend.map((t) => MONTHS[monthIndex(t.month)]),
      labels: { style: { colors: MUTED, fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    // Rank 1 at the top.
    yaxis: { reversed: true, min: 1, max: Math.max(c.of, 2), tickAmount: 2, labels: { show: false } },
    tooltip: {
      theme: 'dark',
      y: { formatter: (v: number, o?: { dataPointIndex: number }) => {
        const t = trend[o?.dataPointIndex ?? 0];
        return `#${v} of ${t.of} · ${short(t.revenue)} · ${plural(t.conversions, 'conversion')}`;
      } },
    },
    legend: { show: false },
  };

  return (
    <Shell title={title} fill>
      <div className="sales-standing__rank">
        <span className="sales-standing__big">#{c.rank}</span>
        <span className="sales-standing__of">of {c.of} {data.region} agent{c.of === 1 ? '' : 's'}</span>
      </div>

      {/* Thin cells for a big region, so the bar always fits. */}
      <div className={`sales-standing__bar${dense ? ' sales-standing__bar--dense' : ''}`} title={`#${c.rank} of ${c.of}`}>
        {Array.from({ length: c.of }, (_, i) => (
          <div key={i} className={`sales-standing__cell${i + 1 === c.rank ? ' sales-standing__cell--me' : ''}`} />
        ))}
      </div>
      <div className="sales-standing__scale sales-standing__muted">
        <span>#1</span>
        <span>Top {c.topPct}%</span>
        <span>#{c.of}</span>
      </div>

      {(movement || gap) && (
        <div className="sales-standing__callout">
          {movement}{movement && gap ? ' · ' : ''}{gap}
        </div>
      )}

      <div className="sales-standing__subtitle">
        6-month trend · rank among {data.region} agents
      </div>
      <Chart type="line" height={190} series={[{ name: 'Rank', data: trend.map((t) => t.rank) }]} options={options} />
      <div className="sales-standing__muted sales-caption">
        Ranked by revenue in the month (so far, for the current one); ties go to more conversions.
      </div>
    </Shell>
  );
};

export default WhereIStand;
